import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { SectionLabel } from './Typography';

export interface SectionNavItem {
  id: string;
  label: string;
  /** Leading glyph. Omit on items that carry a `prefix` instead. */
  icon?: ComponentType<{ className?: string }>;
  /**
   * Short ordinal shown ahead of the label, e.g. a turn number. Gives a long
   * list of similar labels somewhere to anchor, and feeds the header's
   * "12 of 47" position readout.
   */
  prefix?: string;
  /** Hover content, for labels the rail has to truncate. May be rich (e.g. reference badges). */
  title?: ReactNode;
  /**
   * Rendered at the end of the row, beside the link rather than inside it, so
   * it may hold its own control — a status glyph, a count, a button that opens
   * something. Keep it to a glyph or two: the label is what the row is for.
   */
  trailing?: ReactNode;
  /**
   * Pin the item outside the scrolling list, at the top or the bottom. For the
   * jumps worth reaching without scrolling the nav first — the ends of a long
   * chat, say. Pinned items still take part in active tracking.
   */
  pinned?: 'start' | 'end';
  /**
   * Replaces the default activation for an item whose target is not in the
   * DOM — an unloaded chat turn, say. A click calls this instead of pinning
   * the item and scrolling to its anchor; the item also never reads as
   * active, since there is nothing on the page to track.
   */
  onActivate?: () => void;
  /**
   * Mute the row: for items the list carries but the page does not hold yet,
   * so what is actually on screen stays the readable part of a long list.
   */
  dimmed?: boolean;
}

export interface SectionNavHandle {
  /**
   * Highlight a section because the host just moved the reader to it —
   * a keyboard step through the sections, a jump from elsewhere in the page.
   *
   * Does exactly what a click on that row does: the highlight lands at once
   * rather than waiting for the IntersectionObserver, and holds until the
   * reader scrolls. Call it *before* moving the viewport, so the scroll the
   * jump produces cannot re-select something else on the way.
   *
   * An id the nav does not list hands tracking back to the observer: the rail
   * is a window onto the content, and a highlight is better dropped than left
   * pointing somewhere the reader no longer is.
   */
  pin: (sectionId: string) => void;
}

export interface SectionNavProps {
  sections: SectionNavItem[];
  className?: string;
  label?: string;
  /**
   * The element that actually scrolls the section targets. Defaults to the page
   * content region, or the nearest scrollable ancestor of the first target.
   * Pass it explicitly when the nav sits beside a scroll container that is not
   * the page's — the agent transcript, for example, scrolls inside the page
   * rather than moving it.
   */
  scrollRootRef?: RefObject<HTMLElement | null>;
  /**
   * Mirror the active section into the URL hash so it can be linked and
   * restored. Turn it off for navs over transient, append-only content (chat
   * turns): a hash per item floods history and means nothing on reload.
   */
  syncHash?: boolean;
  /**
   * Called with the section id just before the nav scrolls to it. Lets a host
   * that also drives the scroll container stand down first — the chat
   * transcript, for example, has to release its pin to the bottom or it would
   * pull the view straight back.
   */
  onSelect?: (sectionId: string) => void;
  /**
   * How the jump to a section travels. `smooth` suits a page whose sections are
   * a screen or two apart. Use `auto` over long, `content-visibility` content
   * (the chat transcript): a smooth trip across thousands of pixels renders
   * every row on the way, and the transcript's own scroll heuristics read the
   * resulting stream of resize-and-scroll events as content growth rather than
   * as a jump.
   */
  scrollBehavior?: ScrollBehavior;
  /**
   * Total the header counts against, when the list is a window onto something
   * larger: the header then reads "12 of 47" from the active item's `prefix`.
   * Without it the header is just the label.
   */
  total?: number;
  /**
   * Visual chrome for the rail.
   * - `'timeline'` (default) — vertical guide line, a left accent on the
   *   active row, and boxed pinned ends. Settings and admin pages.
   * - `'list'` — one continuous guide across the whole stack (turns and end
   *   jumps), rounded hover/active fill, no left accent and no boxed pins.
   *   The agent chat turns rail.
   */
  appearance?: 'timeline' | 'list';
  /**
   * Rendered under the label in place of the list when there are no sections.
   * Without it an empty nav renders nothing at all, which is right for a nav
   * over page sections — there is nothing to say — but wrong for one that is a
   * fixture of its pane (the chat's turn rail): that has to hold its place from
   * the first frame rather than appear once a first row exists.
   */
  emptyState?: ReactNode;
  'data-testid'?: string;
  /** Imperative access for a host that scrolls the sections itself. */
  ref?: Ref<SectionNavHandle>;
}

const TARGET_DISCOVERY_WINDOW_MS = 2_000;
const BOTTOM_SCROLL_THRESHOLD_PX = 48;
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

type ScrollRoot = HTMLElement | Window;

function isScrollableElement(element: HTMLElement) {
  const { overflowY } = getComputedStyle(element);
  return (
    (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight
  );
}

function resolveScrollRoot(sectionIds: string[], explicitRoot: HTMLElement | null): ScrollRoot {
  if (typeof document === 'undefined') return window;
  if (explicitRoot) return explicitRoot;

  const pageContent = document.querySelector<HTMLElement>('[aria-label="Page content"]');
  if (pageContent && isScrollableElement(pageContent)) {
    return pageContent;
  }

  const firstTarget = sectionIds.length > 0 ? document.getElementById(sectionIds[0]) : null;
  let current = firstTarget?.parentElement ?? null;
  while (current) {
    if (isScrollableElement(current)) return current;
    current = current.parentElement;
  }

  return window;
}

function isNearScrollEnd(scrollRoot: ScrollRoot) {
  if (scrollRoot instanceof Window) {
    const { scrollY, innerHeight } = window;
    const { scrollHeight } = document.documentElement;
    return scrollY + innerHeight >= scrollHeight - BOTTOM_SCROLL_THRESHOLD_PX;
  }

  return (
    scrollRoot.scrollTop + scrollRoot.clientHeight >=
    scrollRoot.scrollHeight - BOTTOM_SCROLL_THRESHOLD_PX
  );
}

function isElementVisibleInScrollRoot(element: HTMLElement, scrollRoot: ScrollRoot) {
  const rect = element.getBoundingClientRect();
  if (scrollRoot instanceof Window) {
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  const rootRect = scrollRoot.getBoundingClientRect();
  return rect.top < rootRect.bottom && rect.bottom > rootRect.top;
}

function shouldPreferLastSection(
  scrollRoot: ScrollRoot,
  lastSectionId: string | null,
): lastSectionId is string {
  if (!lastSectionId) return false;
  if (!isNearScrollEnd(scrollRoot)) return false;

  const lastSection = document.getElementById(lastSectionId);
  if (!lastSection) return false;

  return isElementVisibleInScrollRoot(lastSection, scrollRoot);
}

function scrollSectionIntoView(sectionId: string, behavior: ScrollBehavior) {
  const element = document.getElementById(sectionId);
  if (!element) return;
  element.scrollIntoView({ behavior, block: 'start' });
  element.focus({ preventScroll: true });
}

/** Breathing room left above/below the active item when the nav scrolls itself. */
const ACTIVE_ITEM_MARGIN_PX = 12;

/**
 * Keep the active item in view when the nav is long enough to scroll on its own
 * (a turn rail beside a long chat). Walks up to the nav root at most, and moves
 * only that scroller: `scrollIntoView` would happily scroll the page too, which
 * on a section-per-scroll-position nav means the nav moving the very content it
 * is reporting on.
 */
function revealActiveItem(item: HTMLElement) {
  let scroller = item.parentElement;
  while (scroller && !isScrollableElement(scroller)) {
    if (scroller.dataset.sectionNavRoot !== undefined) return;
    scroller = scroller.parentElement;
  }
  if (!scroller) return;

  const itemRect = item.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const overflowTop = scrollerRect.top + ACTIVE_ITEM_MARGIN_PX - itemRect.top;
  const overflowBottom = itemRect.bottom - (scrollerRect.bottom - ACTIVE_ITEM_MARGIN_PX);
  if (overflowTop > 0) scroller.scrollTop -= overflowTop;
  else if (overflowBottom > 0) scroller.scrollTop += overflowBottom;
}

function SectionNavList({
  sections,
  scrollRootRef,
  syncHash,
  onSelect,
  scrollBehavior,
  label,
  total,
  appearance,
  emptyState,
  ref,
}: {
  sections: SectionNavItem[];
  scrollRootRef: RefObject<HTMLElement | null> | undefined;
  syncHash: boolean;
  onSelect: ((sectionId: string) => void) | undefined;
  scrollBehavior: ScrollBehavior;
  label: string;
  total: number | undefined;
  appearance: 'timeline' | 'list';
  emptyState: ReactNode;
  ref: Ref<SectionNavHandle> | undefined;
}) {
  const isList = appearance === 'list';
  const navigate = useNavigate();
  const location = useLocation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const pinnedIdRef = useRef<string | null>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  // Only sections without `onActivate` have anchors on the page: those are the
  // ones scroll tracking can observe. Rows with custom activation never read
  // as active, so looking their ids up in the DOM would only make the
  // discovery loop search forever for elements that intentionally do not
  // exist.
  const observableIds = useMemo(
    () => sections.filter((s) => s.onActivate === undefined).map((s) => s.id),
    [sections],
  );
  const lastSectionId = observableIds[observableIds.length - 1] ?? null;
  const pinnedStart = sections.filter((s) => s.pinned === 'start');
  const pinnedEnd = sections.filter((s) => s.pinned === 'end');
  const scrolling = sections.filter((s) => s.pinned === undefined);

  const pinSection = (sectionId: string) => {
    pinnedIdRef.current = sectionId;
    setActiveId(sectionId);
  };

  const unpinSection = () => {
    pinnedIdRef.current = null;
  };

  useImperativeHandle(
    ref,
    (): SectionNavHandle => ({
      pin: (sectionId) => {
        if (sectionIds.includes(sectionId)) pinSection(sectionId);
        else unpinSection();
      },
    }),
    // Rebuilt when the section set changes: `pin` validates against it, and a
    // stale list would refuse an id the nav has since started listing.
    [sectionIds],
  );

  useEffect(() => {
    if (!syncHash) return;
    const hashId = location.hash.slice(1);
    if (hashId && sectionIds.includes(hashId)) {
      pinnedIdRef.current = hashId;
      setActiveId(hashId);
    }
  }, [location.hash, sectionIds, syncHash]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleManualScrollIntent = () => {
      unpinSection();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // An alt chord is not a scroll gesture: alt+arrow scrolls nothing on its
      // own, and it is what the chat's turn rail steps with — reading it as
      // manual scrolling would drop the pin that step just set. The chords that
      // do scroll (cmd+arrow on macOS, ctrl+Home/End on Windows) still count.
      if (event.altKey) return;
      if (SCROLL_KEYS.has(event.key)) {
        handleManualScrollIntent();
      }
    };

    window.addEventListener('wheel', handleManualScrollIntent, { passive: true });
    window.addEventListener('touchmove', handleManualScrollIntent, { passive: true });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', handleManualScrollIntent);
      window.removeEventListener('touchmove', handleManualScrollIntent);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof IntersectionObserver === 'undefined' ||
      observableIds.length === 0
    ) {
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let animationFrame: number | null = null;
    let discoveryTimeout: number | null = null;
    let observedTargetKey = '';
    let scrollRoot: ScrollRoot = window;

    const disconnectObserver = () => {
      observer?.disconnect();
      observer = null;
      observedTargetKey = '';
    };

    const disconnectMutationObserver = () => {
      mutationObserver?.disconnect();
      mutationObserver = null;
      if (discoveryTimeout !== null) {
        window.clearTimeout(discoveryTimeout);
        discoveryTimeout = null;
      }
    };

    const collectTargets = () =>
      observableIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);

    const updateActiveFromScroll = () => {
      if (pinnedIdRef.current !== null) return;
      if (shouldPreferLastSection(scrollRoot, lastSectionId)) {
        setActiveId(lastSectionId);
      }
    };

    const attachObserver = () => {
      if (cancelled) return;

      scrollRoot = resolveScrollRoot(observableIds, scrollRootRef?.current ?? null);
      const targets = collectTargets();
      const targetKey = `${scrollRoot instanceof Window ? 'window' : (scrollRoot.getAttribute('aria-label') ?? 'element')}\u0000${targets.map((el) => el.id).join('\u0000')}`;
      if (targets.length === 0 || targetKey === observedTargetKey) {
        return;
      }

      disconnectObserver();
      observedTargetKey = targetKey;
      observer = new IntersectionObserver(
        (entries) => {
          if (pinnedIdRef.current !== null) return;

          if (shouldPreferLastSection(scrollRoot, lastSectionId)) {
            setActiveId(lastSectionId);
            return;
          }

          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          if (visible) setActiveId(visible.target.id);
        },
        {
          root: scrollRoot instanceof Window ? null : scrollRoot,
          rootMargin: '-80px 0px -70% 0px',
          threshold: [0, 1],
        },
      );
      for (const el of targets) observer.observe(el);

      if (targets.length === observableIds.length) {
        disconnectMutationObserver();
      }
    };

    const scheduleAttach = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        attachObserver();
      });
    };

    const handleScroll = () => {
      updateActiveFromScroll();
    };

    attachObserver();
    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });

    if (typeof MutationObserver !== 'undefined') {
      const root =
        scrollRootRef?.current ??
        document.querySelector('[aria-label="Page content"]') ??
        document.body;
      mutationObserver = new MutationObserver(scheduleAttach);
      mutationObserver.observe(root, { childList: true, subtree: true });
      discoveryTimeout = window.setTimeout(disconnectMutationObserver, TARGET_DISCOVERY_WINDOW_MS);
    }

    return () => {
      cancelled = true;
      disconnectObserver();
      disconnectMutationObserver();
      scrollRoot.removeEventListener('scroll', handleScroll);
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [lastSectionId, observableIds, scrollRootRef]);

  const handleSectionClick = (e: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    pinSection(sectionId);
    if (syncHash) {
      void navigate(
        { pathname: location.pathname, search: location.search, hash: `#${sectionId}` },
        { replace: false },
      );
    }
    onSelect?.(sectionId);
    scrollSectionIntoView(sectionId, scrollBehavior);
  };

  useEffect(() => {
    if (!activeId) return;
    const item = itemRefs.current.get(activeId);
    if (item) revealActiveItem(item);
  }, [activeId]);

  const renderItem = (section: SectionNavItem) => {
    const Icon = section.icon;
    const isActive = section.onActivate === undefined && activeId === section.id;
    const link = (
      <a
        ref={(el) => {
          if (el) itemRefs.current.set(section.id, el);
          else itemRefs.current.delete(section.id);
        }}
        href={`#${section.id}`}
        aria-label={`Go to ${section.label}`}
        data-active={isActive || undefined}
        onClick={(e) => {
          if (!section.onActivate) {
            handleSectionClick(e, section.id);
            return;
          }
          // Custom activation: no anchor to pin or scroll to, so only the
          // default-navigation guards apply before handing off.
          if (e.defaultPrevented || e.button !== 0) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          section.onActivate();
        }}
        className={cn(
          'flex min-w-0 flex-1 items-center truncate py-1.5 text-[13px] transition-colors',
          // List: same tight inset + gap for numbered turns and icon actions
          // (Jump to bottom). Timeline keeps pl-3 so labels clear the guide.
          isList ? 'gap-1.5 px-1' : 'gap-2 pl-3 pr-1',
          isActive
            ? 'font-medium text-primary'
            : section.dimmed
              ? 'text-muted-foreground/60 group-hover/nav-item:text-foreground'
              : 'text-muted-foreground group-hover/nav-item:text-foreground',
        )}
      >
        {Icon ? <Icon className="icon-sm shrink-0" aria-hidden /> : null}
        {section.prefix ? (
          <span
            aria-hidden
            className={cn(
              // List: no reserved column — the digit sits where an icon-sm
              // would, matching Jump to bottom. Timeline: right-aligned rail.
              isList
                ? 'icon-sm inline-flex shrink-0 items-center justify-center text-[11px] tabular-nums'
                : 'w-5 shrink-0 text-right text-[11px] tabular-nums',
              isActive ? 'text-primary' : 'text-muted-foreground/60',
            )}
          >
            {section.prefix}
          </span>
        ) : null}
        <span className="truncate">{section.label}</span>
      </a>
    );

    return (
      // The row, not the link, carries the hover and active fill, so it reads as
      // one target however many accessories sit at its end. Timeline uses
      // `ml-px` so the fill clears the guide; list uses `ml-1` for a visible
      // gap between the continuous guide and the rounded rows.
      <li
        key={section.id}
        className={cn(
          'group/nav-item relative flex items-center rounded-md transition-colors',
          isList ? 'ml-1' : 'ml-px',
          isActive ? 'bg-muted/50' : 'hover:bg-muted/40',
        )}
      >
        {!isList && isActive ? (
          <motion.span
            layoutId="section-nav-indicator"
            aria-hidden
            className="absolute -left-px bottom-1 top-1 w-0.5 rounded-full bg-primary"
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
        ) : null}
        {/* The label is truncated to fit the rail, so the full text has to be
            reachable — as a real tooltip rather than the browser's, which waits
            a second and cannot be styled or read on touch. */}
        {section.title ? (
          <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="left" className="max-w-sm whitespace-pre-line">
              {section.title}
            </TooltipContent>
          </Tooltip>
        ) : (
          link
        )}
        {section.trailing ? (
          <span className="flex shrink-0 items-center gap-1 px-1">{section.trailing}</span>
        ) : null}
      </li>
    );
  };

  const activePosition = activeId
    ? (sections.find((section) => section.id === activeId)?.prefix ?? null)
    : null;

  const listStackClass = isList ? 'gap-0.5' : undefined;

  const pinnedStartList =
    pinnedStart.length > 0 ? (
      <ul
        className={cn(
          'relative flex flex-col',
          listStackClass,
          !isList && 'border-b border-border/60 pb-1',
        )}
      >
        {!isList && <span aria-hidden className="absolute left-0 top-0 h-full w-px bg-border" />}
        {pinnedStart.map(renderItem)}
      </ul>
    ) : null;

  // `min-h-0` + `overflow-y-auto` make the list, not the whole nav, the part
  // that scrolls when the nav is given a bounded height: the label and the
  // pinned ends stay put while a long list moves between them. Both are inert
  // until something bounds the nav's height.
  const scrollingList = (
    <ul
      data-section-nav-list=""
      className={cn('relative flex min-h-0 flex-col overflow-y-auto', listStackClass)}
    >
      {!isList && <span aria-hidden className="absolute left-0 top-0 h-full w-px bg-border" />}
      {scrolling.map(renderItem)}
    </ul>
  );

  const pinnedEndList =
    pinnedEnd.length > 0 ? (
      <ul
        className={cn(
          'relative flex flex-col',
          listStackClass,
          !isList && 'border-t border-border/60 pt-1',
        )}
      >
        {!isList && <span aria-hidden className="absolute left-0 top-0 h-full w-px bg-border" />}
        {pinnedEnd.map(renderItem)}
      </ul>
    ) : null;

  return (
    <>
      <div className="flex items-baseline justify-between gap-2 pr-1">
        <SectionLabel as="span" className="text-[11px]">
          {label}
        </SectionLabel>
        {total !== undefined && (
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
            {activePosition ? `${activePosition} of ${total}` : total}
          </span>
        )}
      </div>

      {sections.length === 0 ? (
        // No guide line: a rule down the side of a single muted line reads as a
        // list that failed to load rather than one that is simply empty.
        <p
          className={cn(
            'py-1.5 text-[13px] text-muted-foreground/60',
            // Sits where a row's label would: `ml-1 px-1` in list mode, the
            // guide's clearance in timeline mode.
            isList ? 'ml-1 px-1' : 'pl-3',
          )}
        >
          {emptyState}
        </p>
      ) : isList ? (
        // One continuous guide across earlier-turns, the scrolling turns, and
        // Jump to bottom — list mode drops the boxed pinned chrome, so the
        // line has to span the whole stack rather than restarting per group.
        <div className="relative flex min-h-0 flex-col gap-0.5">
          <span aria-hidden className="absolute inset-y-0 left-0 w-px bg-border" />
          {pinnedStartList}
          {scrollingList}
          {pinnedEndList}
        </div>
      ) : (
        <>
          {pinnedStartList}
          {scrollingList}
          {pinnedEndList}
        </>
      )}
    </>
  );
}

/**
 * Floating section navigation for long pages. Highlights the section currently in
 * view via IntersectionObserver, pins the clicked or hashed section until the
 * user scrolls manually, and prefers the last section when scrolled to the bottom
 * of the page scroll container. When the nav itself is given a bounded height, it
 * scrolls to keep the active item visible.
 *
 * A host that moves the reader between sections itself — the chat transcript's
 * `alt+↑/↓` turn stepping — reports each jump through {@link SectionNavHandle},
 * so the nav highlights it immediately instead of inferring it from the scroll
 * that follows.
 */
export function SectionNav({
  sections,
  className,
  label = 'Sections',
  scrollRootRef,
  syncHash = true,
  onSelect,
  scrollBehavior = 'smooth',
  total,
  appearance = 'timeline',
  emptyState,
  'data-testid': dataTestId = 'section-nav',
  ref,
}: SectionNavProps) {
  const location = useLocation();
  const sectionKey = useMemo(() => sections.map((s) => s.id).join('\u0000'), [sections]);
  const listKey = `${location.pathname}:${sectionKey}`;

  // An empty nav has nothing to show, unless the caller said what to show in
  // place of the list.
  if (sections.length === 0 && emptyState === undefined) return null;

  return (
    <nav
      aria-label={label}
      data-testid={dataTestId}
      data-section-nav-root=""
      data-appearance={appearance}
      className={cn('flex flex-col gap-3 text-sm', className)}
    >
      <SectionNavList
        key={listKey}
        sections={sections}
        scrollRootRef={scrollRootRef}
        syncHash={syncHash}
        onSelect={onSelect}
        scrollBehavior={scrollBehavior}
        label={label}
        total={total}
        appearance={appearance}
        emptyState={emptyState}
        ref={ref}
      />
    </nav>
  );
}
